import React, { useEffect, useState, useCallback, useRef, useLayoutEffect } from "react";
import Tree from "react-d3-tree";
import axios from "axios";
import API_BASE_URL, { BASE_URL } from "../config";
import { Plus } from "lucide-react";
import {
  Button, Typography, Paper, Box, Fade, alpha,
  Avatar, Chip, CircularProgress,
} from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";
import { ArrowBack as ArrowBackIcon } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useMediaQuery } from "@mui/material";
import MemberProfileDialog from "./MemberProfileDialog";

// ─── Styled Components ──────────────────────────────────────────────────────

const StyledPaper = styled(Paper)(({ theme }) => ({
  background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.primary.light, 0.08)} 100%)`,
  borderRadius: theme.spacing(3),
  padding: theme.spacing(3),
  position: "relative",
  overflow: "hidden",
  boxShadow: "0 8px 32px rgba(0,0,0,0.08)",
  minHeight: "90vh",
}));

const TreeContainer = styled(Box)(({ theme }) => ({
  width: "100%",
  height: "78vh",
  position: "relative",
  overflow: "hidden",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  borderRadius: theme.spacing(2),
  background: alpha(theme.palette.background.paper, 0.7),
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0, left: 0, right: 0,
    height: "4px",
    background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
  },
}));

const BackButton = styled(Button)(({ theme }) => ({
  borderRadius: "50%",
  width: 50, height: 50, minWidth: 50,
  boxShadow: `0 4px 20px ${alpha(theme.palette.common.black, 0.15)}`,
}));

// ─── Deduplication helper ────────────────────────────────────────────────────

const deduplicateTree = (node, seen = new Set()) => {
  if (!node) return null;
  const id = String(node.attributes?.id ?? node.id ?? "");

  if (id && id !== "virtual") {
    if (seen.has(id)) return null;
    seen.add(id);
  }

  let spouse = node.attributes?.spouse || null;
  if (spouse) {
    const spouseId = String(spouse.attributes?.id ?? "");
    if (spouseId && seen.has(spouseId)) {
      spouse = null;
    } else if (spouseId) {
      seen.add(spouseId);
    }
  }

  return {
    ...node,
    attributes: { ...node.attributes, spouse },
    children: (node.children || [])
      .map((c) => deduplicateTree(c, seen))
      .filter(Boolean),
  };
};

// ─── Component ──────────────────────────────────────────────────────────────

const ShowFamily = () => {
  const navigate = useNavigate();
  const theme    = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [familyTreeData,    setFamilyTreeData]    = useState(null);
  const [loading,           setLoading]           = useState(true);
  const [error,             setError]             = useState("");
  const [loggedInUserName,  setLoggedInUserName]  = useState("User");
  const [navigationHistory, setNavigationHistory] = useState([]);
  const [isLoadingNode,     setIsLoadingNode]     = useState(false);
  const [dimensions,        setDimensions]        = useState({ width: 0, height: 0 });
  const treeContainerRef = useRef(null);

  const [profileDialog, setProfileDialog] = useState({
    open: false, memberId: null, relationship: "",
  });

  // ── Fetch ─────────────────────────────────────────────────────────────────

  const fetchFamilyData = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const sessionRes = await axios.get(`${API_BASE_URL}/user/id`, { withCredentials: true });
      const userId = sessionRes?.data?.userId;
      if (!userId) throw new Error("User not authenticated");

      // Fetch logged in user's profile for the dynamic heading
      const userRes = await axios.get(`${API_BASE_URL}/user/${userId}`, { withCredentials: true });
      if (userRes.data?.user?.first_name) {
        setLoggedInUserName(userRes.data.user.first_name);
      }

      const response = await axios.get(
        `${API_BASE_URL}/relationship/${userId}`,
        { withCredentials: true }
      );
      if (!response.data) throw new Error("No family tree data found");

      const clean = deduplicateTree(response.data);
      setFamilyTreeData(clean);
      setNavigationHistory([clean]);
    } catch (err) {
      console.error("Family Tree Error:", err);
      setError(err.response?.data?.message || err.message || "Failed to load family tree");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchFamilyData(); }, [fetchFamilyData]);

  // ── Resize observer ───────────────────────────────────────────────────────

  useLayoutEffect(() => {
    const updateDimensions = () => {
      if (treeContainerRef.current) {
        const { width, height } = treeContainerRef.current.getBoundingClientRect();
        setDimensions({
          width,
          height,
        });
      }
    };

    // Ensure initial measurement happens after layout is ready
    const animationFrame = requestAnimationFrame(updateDimensions);

    const ro = new ResizeObserver(updateDimensions);
    if (treeContainerRef.current) ro.observe(treeContainerRef.current);

    return () => {
      cancelAnimationFrame(animationFrame);
      ro.disconnect();
    };
  }, []);

  // ── Handlers ──────────────────────────────────────────────────────────────

  const handleNodeClick = useCallback((id, relationship) => {
    if (!id || id === "virtual") return;
    setProfileDialog({ open: true, memberId: id, relationship: relationship || "" });
  }, []);

  const handleExploreNode = useCallback(async (memberId) => {
    if (!memberId || memberId === "virtual") return;
    try {
      setIsLoadingNode(true);
      const response = await axios.get(
        `${API_BASE_URL}/relationship/${memberId}`,
        { withCredentials: true }
      );
      if (!response.data) return;
      const clean = deduplicateTree(response.data);
      setFamilyTreeData(clean);
      setNavigationHistory((prev) => [...prev, clean]);
    } catch (err) {
      console.error("Explore node error:", err);
    } finally {
      setIsLoadingNode(false);
    }
  }, []);

  const handleBack = useCallback(() => {
    setNavigationHistory((prev) => {
      if (prev.length <= 1) return prev;
      const next = prev.slice(0, -1);
      setFamilyTreeData(next[next.length - 1]);
      return next;
    });
  }, []);

  const handleRefresh = useCallback(() => { fetchFamilyData(); }, [fetchFamilyData]);

  // ── Member card ───────────────────────────────────────────────────────────

  const renderMemberCard = useCallback(
    (node, isSpouse = false) => {
      if (node.attributes?.isVirtual) return null;

      const gender   = node.attributes?.gender || "";
      const isFemale = String(gender).toLowerCase() === "female";
      const isSelf   = node.attributes?.relationship === "Self";

      // Professional color palette
      const primaryColor = theme.palette.primary.main;
      const secondaryColor = theme.palette.secondary.main;
      const selfColor = theme.palette.warning.main;

      const borderColor = isSelf
        ? selfColor
        : isFemale ? secondaryColor : primaryColor;

      // Soft gradients for modern look
      const bgColor = isSelf
        ? `linear-gradient(135deg, ${alpha(selfColor, 0.2)} 0%, ${alpha(selfColor, 0.05)} 100%)`
        : isFemale
        ? `linear-gradient(135deg, ${alpha(secondaryColor, 0.15)} 0%, ${alpha(secondaryColor, 0.05)} 100%)`
        : `linear-gradient(135deg, ${alpha(primaryColor, 0.15)} 0%, ${alpha(primaryColor, 0.05)} 100%)`;

      const rawImg = node.attributes?.imageUrl;
      const imgSrc = rawImg
        ? rawImg.startsWith("http") ? rawImg : `${BASE_URL}${rawImg}`
        : null;

      return (
        <Box
          sx={{
            p: 1.5, width: 170, borderRadius: "20px",
            border: `2px solid ${borderColor}`,
            background: bgColor,
            display: "flex", flexDirection: "column", alignItems: "center",
            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
            cursor: "pointer",
            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            "&:hover": {
              transform: "scale(1.05) translateY(-5px)",
              boxShadow: "0 12px 24px rgba(0,0,0,0.15)",
              borderColor: isSelf ? selfColor : (isFemale ? theme.palette.secondary.light : theme.palette.primary.light),
            },
          }}
          onClick={(e) => {
            e.stopPropagation();
            handleNodeClick(node.attributes?.id || node.id, node.attributes?.relationship);
          }}
        >
          <Avatar
            src={imgSrc}
            sx={{
              width: 55, height: 55, mb: 1,
              bgcolor: isSelf ? "warning.main" : isFemale ? "secondary.main" : "primary.main",
            }}
          >
            {node.name?.charAt(0)?.toUpperCase()}
          </Avatar>

          <Typography variant="subtitle2" fontWeight={700} textAlign="center" sx={{ lineHeight: 1.2, mb: 0.3 }}>
            {node.name}
          </Typography>

          <Typography variant="caption" color="text.secondary" textAlign="center">
            {node.attributes?.relationship || "Family Member"}
          </Typography>

          {isSelf && (
            <Chip label="You" size="small" color="warning" sx={{ mt: 0.5, height: 18, fontSize: "0.6rem" }} />
          )}
          {isSpouse && !isSelf && (
            <Chip label="Spouse" size="small" color="info" sx={{ mt: 0.5, height: 18, fontSize: "0.6rem" }} />
          )}
        </Box>
      );
    },
    [theme, handleNodeClick]
  );

  // ── Custom SVG node ───────────────────────────────────────────────────────

  const renderRectSvgNode = useCallback(
    ({ nodeDatum }) => {
      if (nodeDatum.attributes?.isVirtual) {
        return <g />;
      }

      const spouse    = nodeDatum.attributes?.spouse || null;
      const hasSpouse = !!spouse;
      const cardW     = 170;
      const gapW      = 44;
      const totalW    = hasSpouse ? cardW * 2 + gapW : cardW;
      const totalH    = 155;

      return (
        <g>
          <foreignObject
            width={totalW} height={totalH}
            x={-(totalW / 2)} y={-(totalH / 2)}
            style={{ overflow: "visible" }}
          >
            <div
              xmlns="http://www.w3.org/1999/xhtml"
              style={{
                display: "flex", alignItems: "center",
                justifyContent: "center", gap: "4px",
                height: "100%", width: "100%",
              }}
            >
              {renderMemberCard(nodeDatum)}

              {hasSpouse && (
                <>
                  <div style={{
                    display: "flex", flexDirection: "column",
                    alignItems: "center", gap: "2px",
                    flexShrink: 0, width: gapW,
                  }}>
                    <div style={{ width: "100%", height: 2, background: theme.palette.divider }} />
                    <span style={{ fontSize: "0.6rem", color: theme.palette.text.secondary, fontWeight: 600, whiteSpace: "nowrap" }}>
                      married
                    </span>
                  </div>
                  {renderMemberCard(
                    { name: spouse.name, attributes: spouse.attributes || spouse },
                    true
                  )}
                </>
              )}
            </div>
          </foreignObject>
        </g>
      );
    },
    [renderMemberCard]
  );

  // ── Loading / Error / Empty ────────────────────────────────────────────────

  if (loading) return (
    <Box sx={{ p: 3 }}><StyledPaper>
      <Box sx={{ minHeight: "70vh", display: "flex", justifyContent: "center", alignItems: "center", flexDirection: "column", gap: 3 }}>
        <CircularProgress />
        <Typography variant="h6">Loading Family Tree…</Typography>
      </Box>
    </StyledPaper></Box>
  );

  if (error) return (
    <Box sx={{ p: 3 }}><StyledPaper>
      <Box sx={{ minHeight: "70vh", display: "flex", justifyContent: "center", alignItems: "center", flexDirection: "column", gap: 3 }}>
        <Typography variant="h5" color="error" fontWeight={700}>{error}</Typography>
        <Button variant="contained" onClick={fetchFamilyData}>Retry</Button>
      </Box>
    </StyledPaper></Box>
  );

  if (!familyTreeData) return (
    <Box sx={{ p: 3 }}><StyledPaper>
      <Box sx={{ minHeight: "70vh", display: "flex", justifyContent: "center", alignItems: "center", flexDirection: "column", gap: 3 }}>
        <Typography variant="h5">No Family Tree Found</Typography>
        <Button variant="contained" onClick={() => navigate("/home")}>Add Family Members</Button>
      </Box>
    </StyledPaper></Box>
  );

  const headingName = familyTreeData.attributes?.isVirtual
    ? (familyTreeData.children?.[0]?.name || "Family")
    : familyTreeData.name;

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <>
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Fade in timeout={800}>
          <StyledPaper>
            <Button color="primary" onClick={() => navigate("/home#family-details")}>
              <Plus size={16} style={{ marginRight: 5 }} />
              Add Family Members
            </Button>

            <Box sx={{ textAlign: "center", mb: 4 }}>
              <Typography
                variant="h4" fontWeight={700}
                sx={{
                  background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                  backgroundClip: "text", WebkitBackgroundClip: "text", color: "transparent",
                }}
              >
                {loggedInUserName}'s Family Tree
              </Typography>
              <Typography variant="subtitle1" color="text.secondary">
                Explore your family hierarchy
              </Typography>
            </Box>

            {navigationHistory.length > 1 && (
              <Box sx={{ position: "absolute", top: 20, right: 20 }}>
                <BackButton onClick={handleBack} disabled={isLoadingNode}>
                  <ArrowBackIcon />
                </BackButton>
              </Box>
            )}

            {isLoadingNode && (
              <Box sx={{
                position: "absolute", inset: 0, zIndex: 10, borderRadius: 3,
                display: "flex", alignItems: "center", justifyContent: "center",
                background: alpha(theme.palette.background.paper, 0.6),
              }}>
                <CircularProgress />
              </Box>
            )}

            <TreeContainer ref={treeContainerRef}>
              <Tree
                data={familyTreeData}
                orientation="vertical"
                pathFunc="step"
                translate={{
                  x: dimensions.width / 2,
                  y: dimensions.height / 4,
                }}
                renderCustomNodeElement={renderRectSvgNode}
                zoom={isMobile ? 0.45 : 0.75}
                separation={{ siblings: 1.8, nonSiblings: 2.2 }}
                nodeSize={{ x: 320, y: 240 }}
                scaleExtent={{ min: 0.15, max: 2.5 }}
                enableLegacyTransitions
                transitionDuration={500}
                collapsible={false}
                styles={{ links: { stroke: theme.palette.primary.main, strokeWidth: 2 } }}
                onNodeClick={(nodeData) => {
                  const attrs = nodeData.data?.attributes;
                  if (attrs?.isVirtual) return;
                  const id = attrs?.id;
                  if (id) handleExploreNode(id);
                }}
              />
            </TreeContainer>
          </StyledPaper>
        </Fade>
      </Box>

      <MemberProfileDialog
        open={profileDialog.open}
        memberId={profileDialog.memberId}
        relationship={profileDialog.relationship}
        onClose={() => setProfileDialog({ open: false, memberId: null, relationship: "" })}
        onMemberUpdated={handleRefresh}
      />
    </>
  );
};

export default ShowFamily;